import {
  QueryBusinessDataParams,
  QueryBusinessDataResult,
} from "src/api/business_gmv/interface";
import { isNullOrEmpty } from "@/common/util/helpers";
import { renderCentPrice, renderNumber } from "@/common/render";
import { CardItem } from "components/data_overview_card";
import { BrandCateInfoListItem } from "src/api/business_gmv/interface";
import cloneDeep from "lodash/cloneDeep";
import moment, { Moment } from "moment";

export interface LocalCardItem extends CardItem {
  tableDataIndex?: string;
}

export const cardList: LocalCardItem[] = [
  {
    title: "支付金额",
    key: "allGmv",
    tooltip:
      "统计周期内，授权店铺的在「所选类目」X「所选品牌」的支付金额加和，多个店铺的支付金额不去重，未剔除售后订单",
    changeKey: "gmvChangeRatio",
    tableDataIndex: "gmv",
    isPrice: true,
  },
  {
    title: "支付买家数",
    key: "allPayUv",
    tooltip:
      "统计周期内，授权店铺的在「所选类目」X「所选品牌」的支付买家数加和，多个店铺的支付买家数不去重，未剔除售后订单",
    changeKey: "payUvChangeRatio",
    tableDataIndex: "payUv",
  },
  {
    title: "支付订单数",
    key: "allPayCount",
    tooltip:
      "统计周期内，授权店铺的在「所选类目」X「所选品牌」的订单数，多个店铺的支付订单不去重",
    changeKey: "payCountChangeRatio",
    tableDataIndex: "payCount",
  },
  {
    title: "支付客单价",
    key: "custAveraPrice",
    tooltip:
      "统计周期内，授权店铺在「所选类目」X「所选品牌」的支付金额总数/支付买家数（跨店铺不去重），即平均每个支付买家的支付金额",
    changeKey: "custAveraPriceChange",
    tableDataIndex: "custAveraPrice",
    isPrice: true,
  },
  {
    title: "支付笔单价",
    key: "unitPrice",
    tooltip:
      "统计周期内，授权店铺在「所选类目」X「所选品牌」的支付金额总数/支付订单数（跨店铺不去重），即平均每笔成交的支付金额",
    changeKey: "unitPriceChange",
    tableDataIndex: "unitPrice",
    isPrice: true,
  },
];

export const dataItemColumns = [
  {
    title: "支付金额 (元)",
    tooltip:
      "统计周期内，授权店铺的在「所选类目」X「所选品牌」的支付金额加和，多个店铺的支付金额不去重，未剔除售后订单",
    dataIndex: "gmv",
    render: renderCentPrice,
  },
  {
    title: "支付买家数",
    tooltip:
      "统计周期内，授权店铺的在「所选类目」X「所选品牌」的支付买家数加和，多个店铺的支付买家数不去重，未剔除售后订单",
    dataIndex: "payUv",
    render: renderNumber,
  },
  {
    title: "支付订单数",
    tooltip:
      "统计周期内，授权店铺的在「所选类目」X「所选品牌」的订单数，多个店铺的支付订单不去重",
    dataIndex: "payCount",
    render: renderNumber,
  },
  {
    title: "支付客单价 (元)",
    tooltip:
      "统计周期内，授权店铺在「所选类目」X「所选品牌」的支付金额总数/支付买家数（跨店铺不去重），即平均每个支付买家的支付金额",
    dataIndex: "custAveraPrice",
    render: renderCentPrice,
  },
  {
    title: "支付笔单价 (元)",
    tooltip:
      "统计周期内，授权店铺在「所选类目」X「所选品牌」的支付金额总数/支付订单数（跨店铺不去重），即平均每笔成交的支付金额",
    dataIndex: "unitPrice",
    render: renderCentPrice,
  },
];

export const SELECTED_CARD_LIST_KEY_MAP = {
  allGmv: "gmv",
  allPayUv: "payUv",
  allPayCount: "payCount",
  custAveraPrice: "custAveraPrice",
  unitPrice: "unitPrice",
};

export const SELECTED_CARD_TITLE_MAP = {
  allGmv: "支付金额 (元)",
  allPayUv: "支付买家数",
  allPayCount: "支付订单数",
  custAveraPrice: "支付客单价 (元)",
  unitPrice: "支付笔单价 (元)",
};

export const isEmptyBusinessData = (data: QueryBusinessDataResult) =>
  isNullOrEmpty(data?.allGmv) &&
  isNullOrEmpty(data?.allPayCount) &&
  isNullOrEmpty(data?.allPayUv) &&
  isNullOrEmpty(data?.custAveraPrice) &&
  isNullOrEmpty(data?.custAveraPriceChange) &&
  isNullOrEmpty(data?.gmvChangeRatio) &&
  isNullOrEmpty(data?.gmvInfoList) &&
  isNullOrEmpty(data?.payCountChangeRatio) &&
  isNullOrEmpty(data?.payUvChangeRatio) &&
  isNullOrEmpty(data?.unitPrice) &&
  isNullOrEmpty(data?.unitPriceChange);

export const STICKY_COMMON_CSS =
  "z-50 flex w-[calc(100%-256px)] justify-between border-b border-[#EBECF2] bg-white px-6 py-4";

export const cate2List2Tree = (cate2List: BrandCateInfoListItem[]) => {
  const tree = [];
  const cate1Map = {};
  cate2List.forEach((cate) => {
    cate1Map[cate.cate1Id] = cate1Map[cate.cate1Id] || {
      cate1Id: cate.cate1Id,
      cate1Name: cate.cate1Name,
      children: [],
    };

    if (
      !cate1Map[cate.cate1Id].children.find(
        (item) => item.cate2Id === cate.cate2Id
      )
    ) {
      cate1Map[cate.cate1Id].children.push({
        cate2Id: cate.cate2Id,
        cate2Name: cate.cate2Name,
      });
    }
  });

  Object.keys(cate1Map).forEach((cate1Id) => {
    tree.push(cate1Map[cate1Id]);
  });

  return tree;
};

export interface FrontFormValues {
  customTimeRange: [Moment, Moment];
  month: Moment;
  week: Moment;
  mallId: number[];
  brandId: number;
  cate2Id: number;
  timeSelectType: "month" | "week" | "custom";
}

export const transformFrontFormValues = (values: FrontFormValues) => {
  const {
    customTimeRange,
    month,
    week,
    mallId,
    brandId,
    cate2Id,
    timeSelectType,
  } = values;

  const params: QueryBusinessDataParams = {
    authMallIdList: mallId,
    brandId,
    cate2Id,
  };

  if (
    isNullOrEmpty(mallId) ||
    isNullOrEmpty(brandId) ||
    isNullOrEmpty(cate2Id)
  ) {
    return undefined;
  }

  if (
    customTimeRange &&
    customTimeRange[0] &&
    customTimeRange[1] &&
    timeSelectType === "custom"
  ) {
    params.startTime = customTimeRange[0].format("YYYY-MM-DD");
    params.endTime = customTimeRange[1].format("YYYY-MM-DD");

    return params;
  } else if (month && timeSelectType === "month") {
    params.startTime = month.startOf("M").format("YYYY-MM-DD");
    params.endTime = month.endOf("M").format("YYYY-MM-DD");

    return params;
  } else if (week && timeSelectType === "week") {
    params.startTime = week.startOf("week").format("YYYY-MM-DD");
    params.endTime = week.endOf("week").format("YYYY-MM-DD");

    return params;
  } else {
    return;
  }
};

/** 根据params.startTime和endTime补充数据result中的gmvInfoList */
export const getFixedBusinessData = (
  params: QueryBusinessDataParams,
  data: QueryBusinessDataResult = {}
) => {
  const fixedData = cloneDeep(data);
  const { startTime, endTime } = params;
  const startMoment = moment(startTime);
  const endMoment = moment(endTime);
  const { gmvInfoList } = data;

  fixedData.gmvInfoList = [];

  for (
    let date = startMoment;
    date.isSameOrBefore(endMoment);
    date.add(1, "d")
  ) {
    const dateStr = date.format("YYYY-MM-DD");
    const gmvInfo = gmvInfoList?.find((item) => item.payTime === dateStr);

    if (!gmvInfo) {
      fixedData.gmvInfoList.push({
        payTime: dateStr,
        payCount: 0,
        custAveraPrice: 0,
        gmv: 0,
        payUv: 0,
      });
    } else {
      fixedData.gmvInfoList.push(gmvInfo);
    }
  }

  return fixedData;
};
